import {
  AutoComplete,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Input,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Table,
  Tag,
  Tooltip,
  TreeSelect,
  Typography,
  notification,
} from "antd";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
} from "chart.js";
import dayjs from "dayjs";
import { Bar } from "react-chartjs-2";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import useApi from "../../utils/useApi";
import useApiV2 from "../../utils/useApiV2";
import {
  arvoreDeFiliais,
  clienteDaFilial,
  filiaisDoCliente,
  filiaisDoFiltro,
  periodoSemiaberto,
} from "./analysisHelpers";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ChartTooltip,
  Legend
);

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const formatPct = (v) => `${Number(v).toFixed(1)}%`;

function SummaryMetric({ label, value }) {
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2 }}>
        {value}
      </div>
      <Text type="secondary">{label}</Text>
    </div>
  );
}

export default function AnalysisSection() {
  const { user } = useAuth();
  const {
    getLostSalesSummary,
    getLostSalesReasons,
    getLostSalesLogs,
    getLostSalesProducts,
  } = useApiV2();
  const {
    getNonSaleReasons,
    getLostSalesSellers,
    postLostSalesRegister,
    getLostSalesFetchRegister,
    patchLostSalesRegister,
    deleteLostSalesRegister,
  } = useApi();

  const isAdmin = Boolean(user?.is_superuser) || Boolean(user?.is_client_admin);
  const canManageRegister =
    isAdmin || Boolean(user?.is_admin) || Boolean(user?.is_staff);

  const manualCardRef = useRef(null);

  // ── Branch / product tree data ───────────────────────────────────────────
  const branches = useMemo(
    () => user?.available_branches || [],
    [user?.available_branches]
  );

  const branchTreeData = useMemo(() => arvoreDeFiliais(branches), [branches]);

  // ── Analysis filters state ───────────────────────────────────────────────
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [pickerDates, setPickerDates] = useState(null);
  const [selectedBranches, setSelectedBranches] = useState(() =>
    user?.active_branch_id ? [user.active_branch_id] : []
  );
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Período e filiais só valem depois do "Aplicar"; o produto, que fica
  // dentro do bloco de não-vendas, aplica na hora. Separar o aplicado do
  // editado evita que trocar o produto aplique junto uma data ainda não
  // confirmada.
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    dateRange,
    branches: selectedBranches,
  }));

  const filterTreeData = useMemo(
    () =>
      arvoreDeFiliais(
        branches,
        selectedBranches.length
          ? clienteDaFilial(branches, selectedBranches[0])
          : null
      ),
    [branches, selectedBranches]
  );

  const filiaisAplicadas = useMemo(
    () =>
      filiaisDoFiltro(
        branches,
        appliedFilters.branches,
        user?.active_branch_id
      ),
    [branches, appliedFilters.branches, user?.active_branch_id]
  );
  const clienteAplicado = clienteDaFilial(branches, filiaisAplicadas[0]);

  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [products, setProducts] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [pareto, setPareto] = useState([]);
  const [paretoTotal, setParetoTotal] = useState(0);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const PAGE_SIZE = 50;

  const [loadingPareto, setLoadingPareto] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // ── Manual register state ────────────────────────────────────────────────
  const [editingLogId, setEditingLogId] = useState(null);
  const [manualBranchId, setManualBranchId] = useState(null);
  const [manualDate, setManualDate] = useState(dayjs());
  const [manualErpCode, setManualErpCode] = useState(null);
  const [manualReasonId, setManualReasonId] = useState(null);
  const [manualDetails, setManualDetails] = useState("");
  const [manualProduct, setManualProduct] = useState("");
  const [sellers, setSellers] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [savingManual, setSavingManual] = useState(false);

  useEffect(() => {
    const branchId = user?.active_branch_id;
    if (branchId) {
      setSelectedBranches([branchId]);
      // Devolve o mesmo objeto quando a filial não mudou: um objeto novo
      // dispararia de novo todas as buscas logo ao abrir a página.
      setAppliedFilters((prev) =>
        prev.branches.length === 1 && prev.branches[0] === branchId
          ? prev
          : { ...prev, branches: [branchId] }
      );
    }
  }, [user?.active_branch_id]);

  // ── Init data ────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await getNonSaleReasons();
        setReasons(Array.isArray(r) ? r : []);
      } catch {
        /* silent */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ids = filiaisDoCliente(branches, clienteAplicado);
    if (!ids.length) {
      setProducts([]);
      return;
    }
    getLostSalesProducts({ branch_id: ids })
      .then((p) => setProducts(p?.products || []))
      .catch(() => setProducts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches, clienteAplicado]);

  // ── Analysis data ────────────────────────────────────────────────────────
  const buildParams = useCallback(
    (extra = {}, { withProduct = true } = {}) => {
      const params = {
        ...periodoSemiaberto(appliedFilters.dateRange),
        branch_id: filiaisAplicadas,
      };
      if (withProduct && selectedProduct) params.product = selectedProduct;
      return { ...params, ...extra };
    },
    [appliedFilters.dateRange, filiaisAplicadas, selectedProduct]
  );

  // O resumo ignora o produto: venda registrada na Lista da Vez não leva
  // produto, então filtrar por ele zeraria as vendas.
  // Um contador por bloco: cada busca guarda o número com que saiu e só
  // aplica a resposta se nenhuma busca mais nova do mesmo bloco saiu depois.
  // Sem isso, trocar de produto rápido podia deixar na tela o resultado do
  // produto anterior, se aquela resposta chegasse por último.
  const summarySeq = useRef(0);
  const paretoSeq = useRef(0);
  const logsSeq = useRef(0);

  const fetchSummary = useCallback(async () => {
    const seq = ++summarySeq.current;
    setLoadingSummary(true);
    try {
      const data = await getLostSalesSummary(
        buildParams({}, { withProduct: false })
      );
      if (seq === summarySeq.current) setSummary(data);
    } catch {
      if (seq === summarySeq.current) setSummary(null);
    } finally {
      if (seq === summarySeq.current) setLoadingSummary(false);
    }
  }, [buildParams, getLostSalesSummary]);

  const fetchPareto = useCallback(async () => {
    const seq = ++paretoSeq.current;
    setLoadingPareto(true);
    try {
      const data = await getLostSalesReasons(buildParams());
      if (seq === paretoSeq.current) {
        setPareto(data.pareto || []);
        setParetoTotal(data.total || 0);
      }
    } catch {
      if (seq === paretoSeq.current) setPareto([]);
    } finally {
      if (seq === paretoSeq.current) setLoadingPareto(false);
    }
  }, [buildParams, getLostSalesReasons]);

  const fetchLogs = useCallback(
    async (page = 1) => {
      const seq = ++logsSeq.current;
      setLoadingLogs(true);
      try {
        const data = await getLostSalesLogs(
          buildParams({ page, page_size: PAGE_SIZE })
        );
        if (seq === logsSeq.current) {
          setLogs(data.results || []);
          setLogsTotal(data.count || 0);
          setLogsPage(page);
        }
      } catch {
        if (seq === logsSeq.current) setLogs([]);
      } finally {
        if (seq === logsSeq.current) setLoadingLogs(false);
      }
    },
    [buildParams, getLostSalesLogs]
  );

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  useEffect(() => {
    fetchPareto();
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, selectedProduct]);

  const handleApply = () => {
    setAppliedFilters({ dateRange, branches: selectedBranches });
  };

  // ── Manual register helpers ──────────────────────────────────────────────
  const isRecordEditable = (attended_at) => {
    const today = dayjs();
    const yesterday = today.subtract(1, "day");
    const d = dayjs(attended_at);
    return d.isSame(today, "day") || d.isSame(yesterday, "day");
  };

  const resetManualForm = () => {
    setEditingLogId(null);
    setManualBranchId(null);
    setManualDate(dayjs());
    setManualErpCode(null);
    setManualReasonId(null);
    setManualDetails("");
    setManualProduct("");
    setSellers([]);
  };

  const handleManualBranchChange = async (branchId) => {
    setManualBranchId(branchId);
    setManualErpCode(null);
    setSellers([]);
    if (!branchId) return;
    setLoadingSellers(true);
    try {
      const data = await getLostSalesSellers(branchId);
      setSellers(data?.sellers || []);
    } catch {
      setSellers([]);
    } finally {
      setLoadingSellers(false);
    }
  };

  const handleEditLog = async (record) => {
    try {
      const full = await getLostSalesFetchRegister(record.id);
      setEditingLogId(full.id);
      setManualDate(full.attended_at ? dayjs(full.attended_at) : dayjs());
      setManualReasonId(full.nonsalereason_id ?? null);
      setManualDetails(full.extra_description || "");
      setManualProduct(full.product || "");
      // Load branch + sellers
      await handleManualBranchChange(full.branch_id);
      setManualErpCode(full.erp_code || null);
      manualCardRef.current?.scrollIntoView({ behavior: "smooth" });
    } catch {
      notification.error({ message: "Erro ao carregar registro para edição." });
    }
  };

  const handleSaveManual = async () => {
    if (!manualBranchId || !manualDate || !manualReasonId) {
      notification.warning({
        message: "Filial, data e motivo são obrigatórios.",
      });
      return;
    }
    setSavingManual(true);
    try {
      const payload = {
        nonsalereason_id: manualReasonId,
        erp_code: manualErpCode || null,
        extra_description: manualDetails || null,
        product: manualProduct || null,
      };
      if (editingLogId) {
        if (user?.is_superuser) {
          payload.attended_at = manualDate.format("YYYY-MM-DD");
        }
        await patchLostSalesRegister(editingLogId, payload);
        notification.success({ message: "Registro atualizado com sucesso." });
      } else {
        payload.branch_id = manualBranchId;
        payload.attended_at = manualDate.format("YYYY-MM-DD");
        await postLostSalesRegister(payload);
        notification.success({ message: "Registro salvo com sucesso." });
      }
      resetManualForm();
      fetchSummary();
      fetchPareto();
      fetchLogs(1);
    } catch (err) {
      notification.error({
        message: "Erro ao salvar registro",
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setSavingManual(false);
    }
  };

  const handleDeleteLog = async (id) => {
    try {
      await deleteLostSalesRegister(id);
      notification.success({ message: "Registro excluído." });
      fetchSummary();
      fetchPareto();
      fetchLogs(logsPage);
    } catch (err) {
      notification.error({
        message: "Erro ao excluir registro",
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  // ── Chart data ──────────────────────────────────────────────────────────
  const chartData = useMemo(() => {
    const labels = pareto.map(
      (r) => r.description || `Motivo ${r.nonsalereason_id}`
    );
    return {
      labels,
      datasets: [
        {
          type: "bar",
          label: "Ocorrências",
          data: pareto.map((r) => r.count),
          backgroundColor: "rgba(24, 144, 255, 0.7)",
          yAxisID: "y",
          order: 2,
        },
        {
          type: "line",
          label: "% Acumulado",
          data: pareto.map((r) => r.pct_cumulative),
          borderColor: "#ff4d4f",
          backgroundColor: "transparent",
          pointBackgroundColor: "#ff4d4f",
          yAxisID: "y2",
          order: 1,
        },
      ],
    };
  }, [pareto]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top" },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              if (ctx.dataset.yAxisID === "y2")
                return `% acumulado: ${formatPct(ctx.raw)}`;
              return `Ocorrências: ${ctx.raw}`;
            },
          },
        },
      },
      scales: {
        y: {
          type: "linear",
          position: "left",
          title: { display: true, text: "Ocorrências" },
          beginAtZero: true,
        },
        y2: {
          type: "linear",
          position: "right",
          min: 0,
          max: 100,
          title: { display: true, text: "% Acumulado" },
          grid: { drawOnChartArea: false },
          ticks: { callback: (v) => formatPct(v) },
        },
      },
    }),
    []
  );

  // ── Columns ─────────────────────────────────────────────────────────────
  const summaryStores = new Set(
    (summary?.by_seller || []).map((row) => row.name_store)
  );
  const summaryColumns = [
    {
      title: "Vendedor(a)",
      dataIndex: "name_seller",
      key: "name_seller",
      render: (v) => v || <Text type="secondary">Sem vendedor</Text>,
    },
    ...(summaryStores.size > 1
      ? [{ title: "Loja", dataIndex: "name_store", key: "name_store" }]
      : []),
    {
      title: "Atendimentos",
      dataIndex: "attendances",
      key: "attendances",
      align: "right",
      sorter: (a, b) => a.attendances - b.attendances,
      defaultSortOrder: "descend",
    },
    {
      title: "Vendas",
      dataIndex: "sales",
      key: "sales",
      align: "right",
      sorter: (a, b) => a.sales - b.sales,
    },
    {
      title: "Não-vendas",
      dataIndex: "no_sales",
      key: "no_sales",
      align: "right",
      sorter: (a, b) => a.no_sales - b.no_sales,
    },
    {
      title: "Conversão",
      dataIndex: "conversion_pct",
      key: "conversion_pct",
      align: "right",
      sorter: (a, b) => (a.conversion_pct ?? -1) - (b.conversion_pct ?? -1),
      render: (v) => (v === null ? "—" : formatPct(v)),
    },
  ];

  const paretoColumns = [
    { title: "Cód.", dataIndex: "nonsalereason_id", key: "code", width: 60 },
    { title: "Motivo", dataIndex: "description", key: "description" },
    {
      title: "Ocorrências",
      dataIndex: "count",
      key: "count",
      align: "right",
    },
    {
      title: "% Total",
      dataIndex: "pct_total",
      key: "pct_total",
      align: "right",
      render: formatPct,
    },
    {
      title: "% Acumulado",
      dataIndex: "pct_cumulative",
      key: "pct_cumulative",
      align: "right",
      render: formatPct,
    },
  ];

  const logColumns = [
    {
      title: "Data",
      dataIndex: "attended_at",
      key: "attended_at",
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—"),
      width: 110,
    },
    { title: "Loja", dataIndex: "name_store", key: "name_store" },
    { title: "Vendedor(a)", dataIndex: "name_seller", key: "name_seller" },
    { title: "Motivo", dataIndex: "description", key: "description" },
    {
      title: "Detalhe",
      dataIndex: "extra_description",
      key: "extra_description",
      render: (v) => v || "—",
    },
    {
      title: "Produto",
      dataIndex: "product",
      key: "product",
      render: (v) => (v ? <Tag>{v}</Tag> : "—"),
    },
    ...(canManageRegister
      ? [
          {
            title: "",
            key: "actions",
            width: 130,
            render: (_, record) => {
              const editable =
                user?.is_superuser || isRecordEditable(record.attended_at);
              const disabledTitle =
                "Permitido apenas para registros de hoje ou ontem";
              return (
                <div style={{ display: "flex", gap: 4 }}>
                  <Tooltip title={!editable ? disabledTitle : ""}>
                    <Button
                      size="small"
                      type="link"
                      disabled={!editable}
                      onClick={() => handleEditLog(record)}>
                      Editar
                    </Button>
                  </Tooltip>
                  <Tooltip title={!editable ? disabledTitle : ""}>
                    <Popconfirm
                      title="Excluir registro?"
                      description="Esta ação não pode ser desfeita."
                      onConfirm={() => handleDeleteLog(record.id)}
                      okText="Excluir"
                      okButtonProps={{ danger: true }}
                      cancelText="Cancelar"
                      disabled={!editable}>
                      <Button
                        size="small"
                        type="link"
                        danger
                        disabled={!editable}>
                        Excluir
                      </Button>
                    </Popconfirm>
                  </Tooltip>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      {/* ── Manual register card ──────────────────────────────────── */}
      {canManageRegister && (
        <Card
          ref={manualCardRef}
          title={
            editingLogId
              ? `Editando registro #${editingLogId}`
              : "Registro Manual"
          }>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Text strong>
                Filial <span style={{ color: "#ff4d4f" }}>*</span>
              </Text>
              <div style={{ marginTop: 8 }}>
                <TreeSelect
                  treeData={branchTreeData}
                  value={manualBranchId}
                  onChange={handleManualBranchChange}
                  placeholder="Selecione a filial"
                  style={{ width: "100%" }}
                  disabled={Boolean(editingLogId)}
                  showSearch
                  treeNodeFilterProp="title"
                  treeDefaultExpandAll
                />
              </div>
            </Col>

            <Col xs={24} md={6}>
              <Text strong>
                Data <span style={{ color: "#ff4d4f" }}>*</span>
              </Text>
              <div style={{ marginTop: 8 }}>
                <DatePicker
                  value={manualDate}
                  onChange={setManualDate}
                  format="DD/MM/YYYY"
                  style={{ width: "100%" }}
                  disabled={Boolean(editingLogId) && !user?.is_superuser}
                />
              </div>
            </Col>

            <Col xs={24} md={10}>
              <Text strong>Vendedor(a)</Text>
              <div style={{ marginTop: 8 }}>
                <Select
                  placeholder={
                    manualBranchId
                      ? "Selecione o vendedor"
                      : "Selecione a filial primeiro"
                  }
                  style={{ width: "100%" }}
                  value={manualErpCode}
                  onChange={setManualErpCode}
                  options={sellers.map((s) => ({
                    value: s.erp_code,
                    label: s.nome,
                  }))}
                  loading={loadingSellers}
                  disabled={!manualBranchId}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                />
              </div>
            </Col>

            <Col xs={24} md={10}>
              <Text strong>
                Motivo <span style={{ color: "#ff4d4f" }}>*</span>
              </Text>
              <div style={{ marginTop: 8 }}>
                <Select
                  placeholder="Selecione o motivo"
                  style={{ width: "100%" }}
                  value={manualReasonId}
                  onChange={setManualReasonId}
                  options={reasons
                    .filter((r) => r.is_active)
                    .map((r) => ({
                      value: r.id,
                      label: `${String(r.code).padStart(2, "0")} — ${r.description}`,
                    }))}
                  showSearch
                  optionFilterProp="label"
                />
              </div>
            </Col>

            <Col xs={24} md={8}>
              <Text strong>Produto</Text>
              <div style={{ marginTop: 8 }}>
                <AutoComplete
                  allowClear
                  placeholder="Digite ou selecione"
                  style={{ width: "100%" }}
                  value={manualProduct || undefined}
                  onChange={(v) => setManualProduct(v || "")}
                  options={products.map((p) => ({ value: p }))}
                  filterOption={(input, option) =>
                    option.value.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </div>
            </Col>

            <Col xs={24} md={6}>
              <Text strong>Detalhe</Text>
              <div style={{ marginTop: 8 }}>
                <Input
                  placeholder="Descrição adicional (opcional)"
                  value={manualDetails}
                  onChange={(e) => setManualDetails(e.target.value)}
                />
              </div>
            </Col>

            <Col xs={24}>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <Button
                  type="primary"
                  loading={savingManual}
                  onClick={handleSaveManual}>
                  {editingLogId ? "Salvar alterações" : "Registrar"}
                </Button>
                {editingLogId && (
                  <Button onClick={resetManualForm}>Cancelar</Button>
                )}
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* ── Period analysis: everything below obeys the filter bar ── */}
      <Divider
        orientation="left"
        orientationMargin={0}
        style={{ margin: "24px 0 0" }}>
        <Title level={4} style={{ margin: 0 }}>
          Análise do período
        </Title>
      </Divider>

      <Card size="small">
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Período e filiais valem para o resumo, a análise de não-vendas e os
          registros detalhados abaixo.
        </Text>
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} md={8}>
            <Text strong>Período</Text>
            <div style={{ marginTop: 8 }}>
              <RangePicker
                value={pickerDates ?? dateRange}
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
                onCalendarChange={setPickerDates}
                onChange={(dates) => {
                  setDateRange(
                    dates || [dayjs().startOf("month"), dayjs().endOf("month")]
                  );
                  setPickerDates(null);
                }}
                onOpenChange={(open) => {
                  if (open) setPickerDates([null, null]);
                }}
              />
            </div>
          </Col>

          {isAdmin && (
            <Col xs={24} md={8}>
              <Text strong>Filiais</Text>
              <div style={{ marginTop: 8 }}>
                <TreeSelect
                  treeData={filterTreeData}
                  value={selectedBranches}
                  onChange={setSelectedBranches}
                  treeCheckable
                  showCheckedStrategy={TreeSelect.SHOW_CHILD}
                  placeholder="Todas as filiais"
                  allowClear
                  style={{ width: "100%" }}
                  maxTagCount="responsive"
                  treeDefaultExpandAll
                />
              </div>
            </Col>
          )}

          <Col xs={24} md={4}>
            <Button type="primary" onClick={handleApply}>
              Aplicar
            </Button>
          </Col>
        </Row>
      </Card>

      {/* ── Period summary ────────────────────────────────────────── */}
      <Card title="Resumo do período">
        {loadingSummary && !summary ? (
          <Skeleton active />
        ) : !summary ? (
          <Text type="secondary">Não foi possível carregar o resumo.</Text>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                gap: 32,
                flexWrap: "wrap",
                marginBottom: 20,
              }}>
              <SummaryMetric
                label="atendimentos"
                value={summary.totals.attendances}
              />
              <SummaryMetric label="vendas" value={summary.totals.sales} />
              <SummaryMetric
                label="não-vendas"
                value={summary.totals.no_sales}
              />
              <SummaryMetric
                label="conversão"
                value={
                  summary.totals.conversion_pct === null
                    ? "—"
                    : formatPct(summary.totals.conversion_pct)
                }
              />
            </div>
            <Table
              size="small"
              loading={loadingSummary}
              columns={summaryColumns}
              dataSource={summary.by_seller.map((r) => ({
                ...r,
                key: `${r.name_store}|${r.name_seller}`,
              }))}
              pagination={false}
              locale={{ emptyText: "Nenhum atendimento no período." }}
            />
          </>
        )}
      </Card>

      {/* ── Analysis card ─────────────────────────────────────────── */}
      <Card title="Análise de não-vendas">
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={24} md={6}>
            <Text strong>Produto</Text>
            <div style={{ marginTop: 8 }}>
              <Select
                allowClear
                placeholder="Todos"
                style={{ width: "100%" }}
                value={selectedProduct}
                onChange={(v) => setSelectedProduct(v ?? null)}
                options={products.map((p) => ({ value: p, label: p }))}
              />
            </div>
          </Col>
        </Row>

        {loadingPareto ? (
          <Skeleton active />
        ) : pareto.length === 0 ? (
          <Text type="secondary">
            Nenhum registro de venda perdida no período.
          </Text>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 12,
              }}>
              <Title level={5} style={{ margin: 0 }}>
                Pareto de motivos
              </Title>
              <Text type="secondary">{paretoTotal} ocorrências</Text>
            </div>

            <div style={{ height: 320, marginBottom: 24 }}>
              <Bar data={chartData} options={chartOptions} />
            </div>

            <Table
              size="small"
              columns={paretoColumns}
              dataSource={pareto.map((r, i) => ({ ...r, key: i }))}
              pagination={false}
              style={{ marginBottom: 32 }}
            />
          </>
        )}
      </Card>

      {/* ── Detailed log card ─────────────────────────────────────── */}
      <Card title="Registros detalhados">
        <Table
          size="small"
          loading={loadingLogs}
          columns={logColumns}
          dataSource={logs.map((r) => ({ ...r, key: r.id }))}
          pagination={{
            current: logsPage,
            pageSize: PAGE_SIZE,
            total: logsTotal,
            showSizeChanger: false,
            onChange: (page) => fetchLogs(page),
          }}
          locale={{ emptyText: "Nenhum registro encontrado." }}
        />
      </Card>
    </>
  );
}
